import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Menu, X, User, LogOut, LayoutDashboard, Heart } from "lucide-react";
import { useState } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useWishlist } from "../context/WishlistContext";
import { useSettings } from "../context/SettingsContext";
import { useContent } from "../context/ContentContext";
import { LOGO_URL } from "../lib/api";

export const Navbar = () => {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const { count: wishCount } = useWishlist();
  const { settings } = useSettings();
  const { content } = useContent();
  const logo = settings.logo_image || LOGO_URL;
  const links = [
    { label: content.nav_merchandise, to: "/shop" },
    { label: content.nav_monster_shirts, to: "/monster-shirts" },
  ];
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <header className="sticky top-0 z-50 bg-zinc-950 border-b border-zinc-800" data-testid="navbar">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={logo} alt="MTL" className="h-14 w-14 object-contain" />
          <span className="font-display text-xl sm:text-2xl tracking-tight leading-none hidden sm:block">
            MONSTER TRUCK<br /><span className="text-[#FF3B30]">LORD</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <Link key={l.label} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors duration-200">
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-4">
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="hidden sm:flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#EAB308] hover:text-white transition-colors">
              <LayoutDashboard size={16} /> Admin
            </Link>
          )}
          {user ? (
            <div className="flex items-center gap-3">
              <Link to="/account" data-testid="nav-account" className="hidden sm:flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-zinc-300 hover:text-white transition-colors">
                <User size={16} /> {user.name.split(" ")[0]}
              </Link>
              <button onClick={handleLogout} data-testid="nav-logout" className="text-zinc-400 hover:text-[#FF3B30] transition-colors"><LogOut size={18} /></button>
            </div>
          ) : (
            <Link to="/login" data-testid="nav-login" className="hidden sm:block text-xs font-bold uppercase tracking-[0.2em] text-zinc-300 hover:text-white transition-colors">{content.nav_login}</Link>
          )}

          {user && (
            <Link to="/wishlist" data-testid="nav-wishlist" className="relative p-2 hover:text-[#FF3B30] transition-colors">
              <Heart size={20} />
              {wishCount > 0 && (
                <span data-testid="wishlist-count" className="absolute -top-1 -right-1 bg-[#EAB308] text-black text-[10px] font-bold h-5 w-5 flex items-center justify-center">{wishCount}</span>
              )}
            </Link>
          )}

          <Link to="/cart" data-testid="nav-cart" className="relative p-2 hover:text-[#FF3B30] transition-colors">
            <ShoppingCart size={22} />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-[#FF3B30] text-white text-[10px] font-bold h-5 w-5 flex items-center justify-center">{count}</span>
            )}
          </Link>

          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-zinc-800 bg-zinc-950 px-6 py-4 flex flex-col gap-4" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="text-sm font-bold uppercase tracking-widest text-zinc-300">{l.label}</Link>
          ))}
          {!user && <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-bold uppercase tracking-widest text-[#FF3B30]">{content.nav_login}</Link>}
          {user && <Link to="/account" onClick={() => setOpen(false)} className="text-sm font-bold uppercase tracking-widest text-zinc-300">My Account</Link>}
          {user && <Link to="/wishlist" onClick={() => setOpen(false)} className="text-sm font-bold uppercase tracking-widest text-zinc-300">My Wishlist</Link>}
          {user?.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-sm font-bold uppercase tracking-widest text-[#EAB308]">Admin Dashboard</Link>}
        </div>
      )}
    </header>
  );
};
